import { Autocomplete, TextField } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ResType, Res, ResourceTypeResource } from 'src/app/types/resources.types'

export type ResourceTypeResourceSelectProps = {
  resourceTypes: ResType[]
  assignableResources: Res[]
  value: ResourceTypeResource | null
  onChange: (v: ResourceTypeResource | null) => void
  error?: boolean
  disabled?: boolean
  hideResourceTypeOptions?: boolean
}

export default function ResourceTypeResourceSelect({ resourceTypes, assignableResources, value, onChange, error, disabled, hideResourceTypeOptions }: ResourceTypeResourceSelectProps) {
  const { t } = useTranslation()
  const options: ResourceTypeResource[] = useMemo(() => {
    const rts: ResourceTypeResource[] = resourceTypes //
      .filter((rt) => rt.guestsCompatible/*  && rt.rateCompatible */ && rt.maxAdultsCompatible)
      .map((rt) => ({ resourceType: rt, resource: null }))

    const rs: ResourceTypeResource[] = assignableResources.reduce((p: ResourceTypeResource[], v) => {
      const rt = rts.find((rt) => rt.resourceType.resourceTypeId === v.resourceTypeId)?.resourceType
      if (rt === undefined) return p
      return [...p, { resourceType: rt, resource: v }]
    }, [])

    if (value && value.resource === null) {
      if (!rts.some((rt) => rt.resourceType.resourceTypeId === value.resourceType.resourceTypeId)) {
        if (hideResourceTypeOptions) return [...rs, value]
        return [...rts, value, ...sortResources(rs)]
      }
    } else if (value && value.resource !== null) {
      if (!rs.some((rt) => rt.resource?.resourceId === value.resource?.resourceId)) {
        if (hideResourceTypeOptions) return [...rs, value]
        return [...rts, ...sortResources(rs), value]
      }
    }
    if (hideResourceTypeOptions) return [...sortResources(rs)]
    return [...rts, ...sortResources(rs)]
  }, [resourceTypes, assignableResources, value, hideResourceTypeOptions])

  return (
    <Autocomplete //
      size="small"
      fullWidth
      disableClearable
      options={options}
      value={value as any}
      onChange={(e, v) => onChange(v)}
      groupBy={(option) => {
        if (!hideResourceTypeOptions) {
          if (option.resource === null) {
            return t('pms.booking.reservation.reserved_resource_edit.resource_type')
          } else {
            return t('pms.booking.reservation.reserved_resource_edit.room')
          }
        }
        return ''
      }}
      getOptionLabel={(option) => {
        if (option.resource === null) {
          if (option.assignedResourceType && option.resourceType.resourceTypeId !== option.assignedResourceType?.resourceTypeId)
            return `${option.resourceType.display} > ${option.assignedResourceType.display}`
          else return option.resourceType.display
        } else {
          return `${option.resource.code} (${
            option.assignedResourceType && option.assignedResourceType.resourceTypeId !== option.resourceType.resourceTypeId ? option.assignedResourceType.display : option.resourceType.display
          })`
        }
      }}
      isOptionEqualToValue={(option: ResourceTypeResource, value: ResourceTypeResource) =>
        option.resourceType.resourceTypeId === value.resourceType.resourceTypeId && option.resource?.resourceId === value.resource?.resourceId
      }
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          inputProps={{ ...params.inputProps, readOnly: true }}
          sx={{
            '& .MuiOutlinedInput-input.MuiInputBase-input.MuiInputBase-inputSizeSmall.MuiInputBase-inputAdornedEnd.MuiAutocomplete-input:read-only': {
              backgroundColor: 'transparent!important',
            },
          }}
        />
      )}
      disabled={disabled}
    />
  )
}

const sortResources = (rs: ResourceTypeResource[]): ResourceTypeResource[] => {
  return rs.sort((a, b) => {
    const aResource = a.resource?.code
    const bResource = b.resource?.code
    if (aResource && bResource) {
      if (aResource < bResource) return -1
      else if (aResource > bResource) return 1
    }
    return 0
  })
}
